import { CORS_PROXY, DEFAULT_API_BASE_URL, getApiBaseUrl } from './constants';
import { getWithExpiry, setWithExpiry } from './storage';

const isDev = process.env.NODE_ENV === 'development';

class ApiService {
  constructor() {
    this.timeout = 10000;
    this.cacheSettings = {
      enabled: true,
      ttl: 7
    };
    this.loadCacheSettings();
  }

  loadCacheSettings() {
    try {
      const saved = localStorage.getItem('cache_settings');
      if (saved) this.cacheSettings = JSON.parse(saved);
    } catch {}
  }

  // Convert absolute API URL to local proxy URL for dev mode
  toLocalUrl(url, baseUrl) {
    if (isDev && baseUrl === DEFAULT_API_BASE_URL && url.startsWith(DEFAULT_API_BASE_URL)) {
      return url.replace(DEFAULT_API_BASE_URL, '/api');
    }
    return url;
  }

  getBaseUrl() {
    return getApiBaseUrl().replace(/\/+$/, '');
  }

  createUrl(path) {
    const baseUrl = this.getBaseUrl();
    const normalizedPath = path.startsWith('/') ? path : `/${path}`;
    return `${baseUrl}${normalizedPath}`;
  }

  async makeRequest(url, options = {}, useCache = true, cacheKey = null, cacheTTL = null, baseUrl = this.getBaseUrl()) {
    if (!this.cacheSettings.enabled) useCache = false;

    const isOnline = navigator.onLine;
    const finalCacheKey = cacheKey || `api_${btoa(url)}`;
    const finalTTL = cacheTTL || (this.cacheSettings.ttl * 24 * 60 * 60 * 1000);

    let cachedItem = null;
    if (useCache) {
      try {
        const itemStr = localStorage.getItem(finalCacheKey);
        if (itemStr) {
          const item = JSON.parse(itemStr);
          if (Date.now() - item.timestamp <= finalTTL) {
            return { data: item.data, source: 'cache', cacheInfo: { cacheDate: new Date(item.timestamp).toISOString() } };
          }
          cachedItem = item;
        }
      } catch {}
    }

    if (!isOnline) {
      if (cachedItem) return { data: cachedItem.data, source: 'stale_cache', cacheInfo: { cacheDate: new Date(cachedItem.timestamp).toISOString() } };
      throw new Error('NO_INTERNET');
    }

    // В dev-режиме: сначала локальный прокси, потом CORS-прокси. В production: сначала прямой запрос, потом CORS-прокси.
    const fetchUrl = this.toLocalUrl(url, baseUrl);
    try {
      const response = await this.fetchWithTimeout(fetchUrl, options);
      const data = await this.safeJsonParse(response);
      if (useCache) {
        try { localStorage.setItem(finalCacheKey, JSON.stringify({ data, timestamp: Date.now(), url })); } catch {}
      }
      return { data, source: isDev ? 'proxy' : 'api', cacheInfo: null };
    } catch (directError) {
      // В production всегда пробуем CORS-прокси, если основной запрос не удался.
      // Для localhost/127.0.0.1 CORS-прокси бесполезен: внешнему прокси недоступен локальный адрес.
      const isLocalTarget = /^https?:\/\/(localhost|127\.0\.0\.1)(:\d+)?\//i.test(url);
      if (isLocalTarget) {
        if (cachedItem) return { data: cachedItem.data, source: 'stale_cache', cacheInfo: { cacheDate: new Date(cachedItem.timestamp).toISOString() } };
        throw new Error('API_UNAVAILABLE_LOCALHOST');
      }

      try {
        const proxyUrl = `${CORS_PROXY}${encodeURIComponent(url)}`;
        const response = await this.fetchWithTimeout(proxyUrl, options);
        const wrapped = await this.safeJsonParse(response);
        let data = wrapped;
        if (wrapped && typeof wrapped.contents === 'string') {
          try { data = JSON.parse(wrapped.contents); } catch { data = wrapped.contents; }
        }
        if (useCache) {
          try { localStorage.setItem(finalCacheKey, JSON.stringify({ data, timestamp: Date.now(), url })); } catch {}
        }
        return { data, source: 'proxy', cacheInfo: null };
      } catch {
        if (cachedItem) return { data: cachedItem.data, source: 'stale_cache', cacheInfo: { cacheDate: new Date(cachedItem.timestamp).toISOString() } };
        throw new Error('API_UNAVAILABLE');
      }
    }
  }

  async getNews(from = 0, amount = 10) {
    const baseUrl = this.getBaseUrl();
    const url = this.createUrl(`/news?amount=${amount}&from=${from}`);
    return this.makeRequest(url, {}, true, `news_${from}_${amount}`, 30 * 60 * 1000, baseUrl);
  }

  async getGroups(course) {
    const baseUrl = this.getBaseUrl();
    const url = this.createUrl(`/getgroups/${course}`);
    return this.makeRequest(url, {}, false, null, null, baseUrl);
  }

  async getSchedule(group, date, week = null) {
    let url;
    const baseUrl = this.getBaseUrl();
    if (week) {
      url = this.createUrl(`/getpairsweek?type=group&data=${encodeURIComponent(group)}&week=${encodeURIComponent(week)}`);
    } else {
      const formattedDate = typeof date === 'string' ? date : this.formatDate(date);
      url = this.createUrl(`/getpairs?type=group&data=${encodeURIComponent(group)}&date=${encodeURIComponent(formattedDate)}`);
    }
    return this.makeRequest(url, {}, false, null, null, baseUrl);
  }

  async getPairsTime() {
    const baseUrl = this.getBaseUrl();
    const url = this.createUrl('/getpairstime');
    return this.makeRequest(url, {}, false, null, null, baseUrl);
  }

  async getCourses() {
    const baseUrl = this.getBaseUrl();
    const url = this.createUrl('/getcourses');
    return this.makeRequest(url, {}, false, null, null, baseUrl);
  }

  async getWeekNumbers() {
    const baseUrl = this.getBaseUrl();
    const url = this.createUrl('/weeknumbers');
    return this.makeRequest(url, {}, false, null, null, baseUrl);
  }

  async getTeacherSchedule(teacher, week) {
    const baseUrl = this.getBaseUrl();
    const url = this.createUrl(`/getpairsweek?type=teacher&data=${encodeURIComponent(teacher)}&week=${week}`);
    return this.makeRequest(url, {}, true, `teacher_schedule_${teacher}_${week}`, 60 * 60 * 1000, baseUrl);
  }

  async getAuditorySchedule(auditory, week) {
    const baseUrl = this.getBaseUrl();
    const url = this.createUrl(`/getpairsweek?type=auditory&data=${encodeURIComponent(auditory)}&week=${week}`);
    return this.makeRequest(url, {}, true, `auditory_schedule_${auditory}_${week}`, 60 * 60 * 1000, baseUrl);
  }

  async search(query) {
    const baseUrl = this.getBaseUrl();
    const url = this.createUrl(`/search/${encodeURIComponent(query)}`);
    return this.makeRequest(url, {}, true, `search_${query}`, 10 * 60 * 1000, baseUrl);
  }

  async createUnifiedWindowTicket(payload) {
    const url = this.createUrl('/unified-window/tickets');
    const baseUrl = this.getBaseUrl();
    return this.makeRequest(
      url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      },
      false,
      null,
      null,
      baseUrl,
    );
  }

  async getUnifiedWindowTicket(token) {
    const url = this.createUrl(`/unified-window/tickets/${encodeURIComponent(token)}`);
    const baseUrl = this.getBaseUrl();
    return this.makeRequest(url, {}, false, null, null, baseUrl);
  }

  async getUnifiedWindowTicketsByEmail(contactEmail) {
    const url = this.createUrl(`/unified-window/tickets?contact_email=${encodeURIComponent(contactEmail)}`);
    const baseUrl = this.getBaseUrl();
    return this.makeRequest(url, {}, false, null, null, baseUrl);
  }

  async getUnifiedWindowMessages(token) {
    const url = this.createUrl(`/unified-window/tickets/${encodeURIComponent(token)}/messages`);
    const baseUrl = this.getBaseUrl();
    return this.makeRequest(url, {}, false, null, null, baseUrl);
  }

  async replyUnifiedWindowTicket(token, message, contactName) {
    const url = this.createUrl(`/unified-window/tickets/${encodeURIComponent(token)}/reply`);
    const baseUrl = this.getBaseUrl();
    return this.makeRequest(
      url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ message, contact_name: contactName }),
      },
      false,
      null,
      null,
      baseUrl,
    );
  }

  async closeUnifiedWindowTicket(token, comment, contactName) {
    const url = this.createUrl(`/unified-window/tickets/${encodeURIComponent(token)}/close`);
    const baseUrl = this.getBaseUrl();
    return this.makeRequest(
      url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ comment, contact_name: contactName }),
      },
      false,
      null,
      null,
      baseUrl,
    );
  }

  async deleteUnifiedWindowTicket(token) {
    const url = this.createUrl(`/unified-window/tickets/${encodeURIComponent(token)}`);
    const baseUrl = this.getBaseUrl();
    return this.makeRequest(
      url,
      { method: 'DELETE' },
      false,
      null,
      null,
      baseUrl,
    );
  }

  formatDate(date) {
    return date.toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit', year: 'numeric' }).replace(/\./g, '.');
  }

  async fetchWithTimeout(url, options = {}) {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), this.timeout);
    try {
      const response = await fetch(url, { ...options, signal: controller.signal });
      clearTimeout(timeoutId);
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      return response;
    } catch (error) {
      clearTimeout(timeoutId);
      throw error;
    }
  }

  async safeJsonParse(response) {
    const text = await response.text();
    try {
      return JSON.parse(text);
    } catch {
      if (text.includes('<')) throw new Error('HTML_RESPONSE');
      throw new Error('JSON_PARSE_ERROR');
    }
  }
}

const apiService = new ApiService();
export default apiService;
